/**
 * TwoFactorCard — Vincent #208 (Eric 2026-05-28)
 *
 * Composant 2FA inclus dans SettingsPage. Gère :
 *   - Affichage du statut (activé / désactivé)
 *   - Lancement du setup (génère QR + secret manuel)
 *   - Vérification du code à 6 chiffres
 *   - Affichage des codes de secours (1 seule fois, à conserver)
 *   - Désactivation (exige mot de passe + code courant)
 *   - Régénération des codes de secours
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, QrCode, Key, FloppyDisk, X as XIcon, ArrowsClockwise,
  Warning, CheckCircle, SpinnerGap, Copy, DeviceMobile,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useLang } from "../contexts/LanguageContext";
import { API_BASE as API } from "../lib/apiBase";

export default function TwoFactorCard() {
  const { lang } = useLang();
  const [status, setStatus] = useState({ enabled: false, backup_codes_remaining: 0 });
  const [loading, setLoading] = useState(true);
  // Setup flow
  const [setupData, setSetupData] = useState(null); // {secret, qr_data_url, otpauth_uri}
  const [setupCode, setSetupCode] = useState("");
  const [setupBusy, setSetupBusy] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState(null);
  // Disable flow
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disableBusy, setDisableBusy] = useState(false);
  // Regen flow
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenPwd, setRegenPwd] = useState("");
  const [regenCode, setRegenCode] = useState("");
  const [regenBusy, setRegenBusy] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/auth/2fa/status`, { withCredentials: true });
      setStatus(data);
    } catch { /* unauth — silencieux */ } finally { setLoading(false); }
  };

  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setSetupBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/2fa/setup`, {}, { withCredentials: true });
      setSetupData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSetupBusy(false); }
  };

  const verifySetup = async () => {
    if (!setupCode.trim()) return toast.error(lang === "en" ? "Enter the 6-digit code" : "Entre le code à 6 chiffres");
    setSetupBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/2fa/verify-setup`, { code: setupCode.trim() }, { withCredentials: true });
      setNewBackupCodes(data.backup_codes);
      setSetupData(null);
      setSetupCode("");
      await loadStatus();
      toast.success(lang === "en" ? "2FA enabled!" : "2FA activé !");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code invalide");
    } finally { setSetupBusy(false); }
  };

  const cancelSetup = () => {
    setSetupData(null);
    setSetupCode("");
  };

  const doDisable = async () => {
    setDisableBusy(true);
    try {
      await axios.post(`${API}/api/auth/2fa/disable`, { password: disablePwd, totp_code: disableCode }, { withCredentials: true });
      toast.success(lang === "en" ? "2FA disabled" : "2FA désactivé");
      setDisableOpen(false);
      setDisablePwd("");
      setDisableCode("");
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setDisableBusy(false); }
  };

  const doRegen = async () => {
    setRegenBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/2fa/regenerate-backup-codes`,
        { password: regenPwd, totp_code: regenCode }, { withCredentials: true });
      setNewBackupCodes(data.backup_codes);
      setRegenOpen(false);
      setRegenPwd("");
      setRegenCode("");
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setRegenBusy(false); }
  };

  const copyAll = (codes) => {
    navigator.clipboard?.writeText(codes.join("\n")).then(
      () => toast.success(lang === "en" ? "Codes copied" : "Codes copiés"),
      () => toast.error("Échec copie"),
    );
  };

  if (loading) {
    return <div className="bg-white border border-zinc-200 rounded-sm p-6 text-center text-zinc-500"><SpinnerGap size={20} className="inline animate-spin" /></div>;
  }

  // STATE 1 : Backup codes display (après setup OU regen)
  if (newBackupCodes) {
    return (
      <div className="bg-white border-2 border-teal-200 rounded-sm p-6" data-testid="backup-codes-display">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={24} weight="fill" className="text-teal-600" />
          <h3 className="font-semibold text-lg">{lang === "en" ? "Backup codes" : "Codes de secours"}</h3>
        </div>
        <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-start gap-2 mb-4">
          <Warning size={16} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
          <div>
            <strong>{lang === "en" ? "Save them NOW." : "Conserve-les MAINTENANT."}</strong>
            {" "}
            {lang === "en"
              ? "These codes won't be shown again. Each one works only once. Use them if you lose your phone."
              : "Ces codes ne seront plus affichés. Chacun ne fonctionne qu'une seule fois. Utilise-les si tu perds ton téléphone."}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 font-mono text-sm mb-4" data-testid="backup-codes-list">
          {newBackupCodes.map((c, i) => (
            <div key={i} className="px-3 py-2 bg-zinc-50 border border-zinc-200 rounded text-center select-all">{c}</div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-1.5" onClick={() => copyAll(newBackupCodes)} data-testid="copy-backup-codes">
            <Copy size={14} /> {lang === "en" ? "Copy all" : "Tout copier"}
          </Button>
          <Button className="flex-1 bg-teal-600 hover:bg-teal-700" onClick={() => setNewBackupCodes(null)} data-testid="ack-backup-codes">
            <CheckCircle size={16} className="mr-1.5" /> {lang === "en" ? "I have saved them" : "Je les ai conservés"}
          </Button>
        </div>
      </div>
    );
  }

  // STATE 2 : Setup en cours (QR affiché)
  if (setupData) {
    return (
      <div className="bg-white border-2 border-blue-200 rounded-sm p-6" data-testid="2fa-setup-card">
        <div className="flex items-center gap-2 mb-4">
          <QrCode size={24} weight="duotone" className="text-[#002FA7]" />
          <h3 className="font-semibold text-lg">{lang === "en" ? "Scan with your authenticator app" : "Scanne avec ton application d'authentification"}</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <img src={setupData.qr_data_url} alt="QR Code" className="w-full max-w-xs border border-zinc-200 rounded" data-testid="2fa-qr-image" />
            <p className="text-xs text-zinc-500 mt-2">
              {lang === "en"
                ? "Compatible: Google Authenticator, Authy, 1Password, Apple Passwords, Microsoft Authenticator."
                : "Compatible : Google Authenticator, Authy, 1Password, Mots de passe Apple, Microsoft Authenticator."}
            </p>
            <details className="mt-3 text-xs text-zinc-600">
              <summary className="cursor-pointer">{lang === "en" ? "Can't scan? Type the secret manually" : "Impossible de scanner ? Tape le secret manuellement"}</summary>
              <div className="mt-2 font-mono text-[11px] bg-zinc-50 border border-zinc-200 rounded p-2 select-all break-all" data-testid="2fa-secret">
                {setupData.secret}
              </div>
            </details>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{lang === "en" ? "Enter the 6-digit code from your app" : "Entre le code à 6 chiffres de ton app"}</label>
            <Input
              value={setupCode}
              onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              autoFocus
              inputMode="numeric"
              className="text-center text-2xl font-mono tracking-widest h-14"
              data-testid="2fa-setup-code-input"
              onKeyDown={(e) => { if (e.key === "Enter" && setupCode.length === 6) verifySetup(); }}
            />
            <div className="flex gap-2 mt-3">
              <Button variant="outline" onClick={cancelSetup} disabled={setupBusy} data-testid="2fa-setup-cancel">
                <XIcon size={14} className="mr-1" /> {lang === "en" ? "Cancel" : "Annuler"}
              </Button>
              <Button onClick={verifySetup} disabled={setupCode.length !== 6 || setupBusy} className="flex-1 bg-[#002FA7] hover:bg-[#002280] gap-1.5" data-testid="2fa-setup-verify">
                {setupBusy ? <SpinnerGap size={14} className="animate-spin" /> : <FloppyDisk size={14} />}
                {lang === "en" ? "Activate 2FA" : "Activer 2FA"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // STATE 3 : Statut + boutons
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="2fa-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <DeviceMobile size={22} className="text-[#002FA7]" />
          <h3 className="font-semibold text-base">{lang === "en" ? "Two-Factor Authentication (2FA)" : "Authentification à deux facteurs (2FA)"}</h3>
        </div>
        {status.enabled ? (
          <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-teal-100 text-teal-700" data-testid="2fa-status-badge">
            {lang === "en" ? "Enabled" : "Activé"}
          </span>
        ) : (
          <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-zinc-200 text-zinc-600" data-testid="2fa-status-badge">
            {lang === "en" ? "Disabled" : "Désactivé"}
          </span>
        )}
      </div>
      <p className="text-sm text-zinc-600 mb-4">
        {lang === "en"
          ? "Add a second layer of security: even with your password, an attacker would need the 6-digit code from your phone."
          : "Ajoute une couche de sécurité : même avec ton mot de passe, un attaquant aurait besoin du code à 6 chiffres de ton téléphone."}
      </p>

      {!status.enabled ? (
        <Button onClick={startSetup} disabled={setupBusy} className="bg-[#002FA7] hover:bg-[#002280] gap-1.5" data-testid="2fa-enable-btn">
          {setupBusy ? <SpinnerGap size={14} className="animate-spin" /> : <ShieldCheck size={16} />}
          {lang === "en" ? "Enable 2FA" : "Activer 2FA"}
        </Button>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-zinc-500">
            {lang === "en"
              ? `${status.backup_codes_remaining} backup code(s) remaining.`
              : `${status.backup_codes_remaining} code(s) de secours restant(s).`}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setRegenOpen(true)} className="gap-1.5" data-testid="2fa-regen-btn">
              <ArrowsClockwise size={14} /> {lang === "en" ? "Regenerate backup codes" : "Régénérer les codes de secours"}
            </Button>
            <Button variant="destructive" onClick={() => setDisableOpen(true)} className="gap-1.5" data-testid="2fa-disable-btn">
              <XIcon size={14} /> {lang === "en" ? "Disable 2FA" : "Désactiver 2FA"}
            </Button>
          </div>
        </div>
      )}

      {/* Disable modal */}
      {disableOpen && (
        <Modal title={lang === "en" ? "Disable 2FA" : "Désactiver le 2FA"} onClose={() => setDisableOpen(false)} testid="disable-2fa-modal">
          <p className="text-xs text-zinc-600 mb-3">{lang === "en" ? "Confirm with your password AND a current 2FA code (TOTP or backup)." : "Confirmé avec ton mot de passe ET un code 2FA actuel (TOTP ou code de secours)."}</p>
          <Input type="password" placeholder={lang === "en" ? "Current password" : "Mot de passe actuel"} value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)} className="mb-2" data-testid="disable-pwd-input" />
          <Input placeholder={lang === "en" ? "TOTP code or backup code" : "Code TOTP ou code de secours"} value={disableCode} onChange={(e) => setDisableCode(e.target.value)} className="mb-3 font-mono" data-testid="disable-code-input" />
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setDisableOpen(false)} disabled={disableBusy}>{lang === "en" ? "Cancel" : "Annuler"}</Button>
            <Button variant="destructive" className="flex-1" onClick={doDisable} disabled={!disablePwd || !disableCode || disableBusy} data-testid="confirm-disable-btn">
              {disableBusy ? <SpinnerGap size={14} className="animate-spin" /> : <XIcon size={14} className="mr-1" />}
              {lang === "en" ? "Disable" : "Désactiver"}
            </Button>
          </div>
        </Modal>
      )}

      {/* Regen modal */}
      {regenOpen && (
        <Modal title={lang === "en" ? "Regenerate backup codes" : "Régénérer les codes de secours"} onClose={() => setRegenOpen(false)} testid="regen-2fa-modal">
          <p className="text-xs text-zinc-600 mb-3">{lang === "en" ? "Old codes will be invalidated immediately." : "Les anciens codes seront invalidés immédiatement."}</p>
          <Input type="password" placeholder={lang === "en" ? "Current password" : "Mot de passe actuel"} value={regenPwd} onChange={(e) => setRegenPwd(e.target.value)} className="mb-2" data-testid="regen-pwd-input" />
          <Input placeholder={lang === "en" ? "Current TOTP code (6 digits)" : "Code TOTP courant (6 chiffres)"} value={regenCode} onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="mb-3 font-mono text-center text-lg" maxLength={6} inputMode="numeric" data-testid="regen-code-input" />
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setRegenOpen(false)} disabled={regenBusy}>{lang === "en" ? "Cancel" : "Annuler"}</Button>
            <Button className="flex-1 bg-[#002FA7] hover:bg-[#002280]" onClick={doRegen} disabled={!regenPwd || regenCode.length !== 6 || regenBusy} data-testid="confirm-regen-btn">
              {regenBusy ? <SpinnerGap size={14} className="animate-spin" /> : <ArrowsClockwise size={14} className="mr-1" />}
              {lang === "en" ? "Regenerate" : "Régénérer"}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}


function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold flex items-center gap-2"><Key size={18} /> {title}</h4>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded"><XIcon size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
